import {
  mapConversationMessages,
  type HubConversationMessage,
  type IDAChatMessage,
} from "./chatMessageMapper";

export const CONVERSATION_MESSAGE_PAGE_SIZE = 20;

export interface ConversationMessagePage {
  items: HubConversationMessage[];
  total?: number;
  hasMore: boolean;
  nextBeforeId: number | null;
  startsMidCluster: boolean;
  legacyComplete: boolean;
  activeJobs?: ActiveConversationJobSummary[] | null;
  activeJobsError?: string | null;
}

export interface ActiveConversationJobSummary {
  jobId: string;
  kind: string;
  status: string;
  progress: number | null;
}

export interface ConversationMessageWindow {
  rawMessages: HubConversationMessage[];
  cachedOlderSegments: ConversationMessageSegment[];
  total?: number;
  hasMore: boolean;
  nextBeforeId: number | null;
  startsMidCluster: boolean;
  legacyComplete: boolean;
}

export interface ConversationMessageSegment {
  rawMessages: HubConversationMessage[];
  hasMore: boolean;
  nextBeforeId: number | null;
  startsMidCluster: boolean;
  legacyComplete: boolean;
}

export interface ConversationMessageGapReconnection {
  window: ConversationMessageWindow;
  pagesFetched: number;
  reachedLimit: boolean;
}

export const MAX_MESSAGE_GAP_RECONNECT_PAGES = 5;

/**
 * FE #1482: an agent-mode turn can fill the newest page entirely with hidden
 * rows (`visible_in_chat: false`), so the mapped window is empty while older
 * pages hold the user's real messages. Automatically walk back over a bounded
 * number of extra pages instead of leaving the chat empty behind a "Load
 * earlier" button.
 */
export const MAX_HIDDEN_PAGE_BACKFILL_PAGES = 5;

export interface HiddenPageBackfillResult {
  window: ConversationMessageWindow;
  pagesFetched: number;
  /** True when the bound was hit while the window still mapped to nothing. */
  reachedLimit: boolean;
}

type MessagePageWire = {
  items?: unknown;
  total?: unknown;
  has_more?: unknown;
  next_before_id?: unknown;
  starts_mid_cluster?: unknown;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function hasOwn(value: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}

function isPositiveSafeInteger(value: unknown): value is number {
  return Number.isSafeInteger(value) && (value as number) > 0;
}

function parseActiveJobs(value: Record<string, unknown>): {
  activeJobs?: ActiveConversationJobSummary[] | null;
  activeJobsError?: string | null;
} {
  if (!hasOwn(value, "active_jobs")) return {};
  if (value.active_jobs === null) {
    return {
      activeJobs: null,
      activeJobsError:
        typeof value.active_jobs_error === "string"
          ? value.active_jobs_error
          : "unavailable",
    };
  }
  if (!Array.isArray(value.active_jobs)) {
    return { activeJobs: null, activeJobsError: "unavailable" };
  }
  const jobs: ActiveConversationJobSummary[] = [];
  for (const candidate of value.active_jobs) {
    if (
      !isRecord(candidate) ||
      typeof candidate.job_id !== "string" ||
      typeof candidate.kind !== "string" ||
      typeof candidate.status !== "string" ||
      (typeof candidate.progress !== "number" && candidate.progress !== null)
    ) {
      return { activeJobs: null, activeJobsError: "unavailable" };
    }
    jobs.push({
      jobId: candidate.job_id,
      kind: candidate.kind,
      status: candidate.status,
      progress: candidate.progress,
    });
  }
  return {
    activeJobs: jobs,
    activeJobsError:
      typeof value.active_jobs_error === "string"
        ? value.active_jobs_error
        : null,
  };
}

/** Parse the additive page contract, while accepting the legacy full response. */
export function parseConversationMessagePage(
  payload: unknown,
  options: { includeActiveJobs?: boolean } = {},
): ConversationMessagePage {
  if (!isRecord(payload)) {
    throw new Error("Invalid conversation message response");
  }

  const value = payload as MessagePageWire & Record<string, unknown>;
  if (!Array.isArray(value.items)) {
    throw new Error("Conversation message response is missing items");
  }
  const paginationKeys = ["has_more", "next_before_id", "starts_mid_cluster"];
  const hasPaginationMetadata = paginationKeys.some((key) =>
    hasOwn(value, key),
  );
  const parsedActiveJobFields = parseActiveJobs(value);
  const activeJobFields =
    options.includeActiveJobs && parsedActiveJobFields.activeJobs === undefined
      ? { activeJobs: null, activeJobsError: "unavailable" }
      : parsedActiveJobFields;

  if (!hasPaginationMetadata) {
    if (!Number.isSafeInteger(value.total) || (value.total as number) < 0) {
      throw new Error("Conversation message response has an invalid total");
    }
    return {
      items: value.items as HubConversationMessage[],
      total: value.total as number,
      hasMore: false,
      nextBeforeId: null,
      startsMidCluster: false,
      legacyComplete: true,
      ...activeJobFields,
    };
  }

  if (
    typeof value.has_more !== "boolean" ||
    typeof value.starts_mid_cluster !== "boolean" ||
    !hasOwn(value, "next_before_id") ||
    (value.next_before_id !== null &&
      !isPositiveSafeInteger(value.next_before_id)) ||
    (value.has_more && !isPositiveSafeInteger(value.next_before_id))
  ) {
    throw new Error(
      "Conversation message response has invalid pagination metadata",
    );
  }

  if (
    value.total !== undefined &&
    (!Number.isSafeInteger(value.total) || (value.total as number) < 0)
  ) {
    throw new Error("Conversation message response has an invalid total");
  }

  if (value.items.length > CONVERSATION_MESSAGE_PAGE_SIZE) {
    throw new Error(
      "Conversation message response exceeds the requested page size",
    );
  }

  if (value.has_more) {
    const oldestReturnedId = value.items[0]?.id;
    if (
      !isPositiveSafeInteger(oldestReturnedId) ||
      value.next_before_id !== oldestReturnedId
    ) {
      throw new Error(
        "Conversation message response has an invalid next_before_id",
      );
    }
  } else if (value.next_before_id !== null) {
    throw new Error(
      "Conversation message response has an invalid next_before_id",
    );
  }

  return {
    items: value.items as HubConversationMessage[],
    total: value.total as number | undefined,
    hasMore: value.has_more,
    nextBeforeId: value.next_before_id as number | null,
    startsMidCluster: value.starts_mid_cluster,
    legacyComplete: false,
    ...activeJobFields,
  };
}

export function buildConversationMessagePageUrl(
  endpoint: string,
  beforeId?: number,
  includeActiveJobs = false,
): string {
  if (beforeId !== undefined && !isPositiveSafeInteger(beforeId)) {
    throw new Error("beforeId must be a positive safe integer");
  }

  const params = new URLSearchParams({
    page_size: String(CONVERSATION_MESSAGE_PAGE_SIZE),
  });
  if (beforeId !== undefined) {
    params.set("before_id", String(beforeId));
  }
  if (includeActiveJobs && beforeId === undefined) {
    params.set("include_active_jobs", "true");
  }
  return `${endpoint}${endpoint.includes("?") ? "&" : "?"}${params.toString()}`;
}

function rawMessageKey(message: HubConversationMessage): string {
  return String(message.id);
}

function rawMessagesOverlap(
  left: HubConversationMessage[],
  right: HubConversationMessage[],
): boolean {
  if (left.length === 0 || right.length === 0) return false;
  const leftIds = new Set(left.map(rawMessageKey));
  return right.some((message) => leftIds.has(rawMessageKey(message)));
}

function sortableMessageId(message: HubConversationMessage): number {
  const value =
    typeof message.id === "number"
      ? message.id
      : /^\d+$/.test(message.id)
        ? Number(message.id)
        : Number.NaN;
  return Number.isSafeInteger(value) ? value : Number.POSITIVE_INFINITY;
}

function sortableCreatedAt(message: HubConversationMessage): number {
  const value = message.created_at
    ? Date.parse(message.created_at)
    : Number.NaN;
  return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
}

function mergeRawMessages(
  current: HubConversationMessage[],
  incoming: HubConversationMessage[],
): HubConversationMessage[] {
  const byId = new Map<string, HubConversationMessage>();
  current.forEach((message) => byId.set(rawMessageKey(message), message));
  // A freshly fetched row wins so persisted enrichment and visibility changes
  // replace an older copy of the same Hub message.
  incoming.forEach((message) => byId.set(rawMessageKey(message), message));

  return Array.from(byId.values())
    .map((message, index) => ({ message, index }))
    .sort((left, right) => {
      // The bounded endpoint guarantees raw Hub insertion/id order. Keep that
      // ordering when pages overlap even if a migrated row carries a clock
      // timestamp that differs from its insertion position.
      const idDiff =
        sortableMessageId(left.message) - sortableMessageId(right.message);
      if (Number.isFinite(idDiff) && idDiff !== 0) return idDiff;

      const timeDiff =
        sortableCreatedAt(left.message) - sortableCreatedAt(right.message);
      if (Number.isFinite(timeDiff) && timeDiff !== 0) return timeDiff;
      return left.index - right.index;
    })
    .map(({ message }) => message);
}

export function replaceConversationMessageWindow(
  page: ConversationMessagePage,
): ConversationMessageWindow {
  return {
    rawMessages: page.items,
    cachedOlderSegments: [],
    total: page.total,
    hasMore: page.hasMore,
    nextBeforeId: page.nextBeforeId,
    startsMidCluster: page.startsMidCluster,
    legacyComplete: page.legacyComplete,
  };
}

/**
 * Reconnect a newest-page discontinuity without issuing an unbounded drain.
 * The implementation is supplied with a page fetcher so the state machine is
 * independently testable from the React polling lifecycle.
 */
export async function reconnectConversationMessageGap(
  current: ConversationMessageWindow,
  fetchOlder: (beforeId: number) => Promise<ConversationMessagePage>,
  maxPages = MAX_MESSAGE_GAP_RECONNECT_PAGES,
): Promise<ConversationMessageGapReconnection> {
  let window = current;
  let pagesFetched = 0;
  const visitedCursors = new Set<number>();

  while (
    window.cachedOlderSegments.length > 0 &&
    window.hasMore &&
    window.nextBeforeId !== null &&
    pagesFetched < maxPages &&
    !visitedCursors.has(window.nextBeforeId)
  ) {
    const beforeId = window.nextBeforeId;
    visitedCursors.add(beforeId);
    window = prependOlderConversationMessagePage(
      window,
      await fetchOlder(beforeId),
    );
    pagesFetched += 1;
  }

  return {
    window,
    pagesFetched,
    reachedLimit:
      window.cachedOlderSegments.length > 0 &&
      window.hasMore &&
      window.nextBeforeId !== null &&
      pagesFetched >= maxPages,
  };
}

/**
 * Load older pages, bounded, until the window maps to at least one visible
 * message or there are no more pages. `onWindowChange` is called after each
 * prepended page so the UI can render progressively instead of waiting for
 * the whole backfill. A fetch failure propagates on the already-published
 * window state (the caller reports it); superseded-generation checks belong
 * to the caller, which owns the generation counter.
 */
export async function backfillConversationMessageWindowUntilVisible({
  window: initialWindow,
  fetchOlder,
  maxPages = MAX_HIDDEN_PAGE_BACKFILL_PAGES,
  onWindowChange,
}: {
  window: ConversationMessageWindow;
  fetchOlder: (beforeId: number) => Promise<ConversationMessagePage>;
  maxPages?: number;
  onWindowChange?: (window: ConversationMessageWindow) => void;
}): Promise<HiddenPageBackfillResult> {
  let window = initialWindow;
  let pagesFetched = 0;
  const visitedCursors = new Set<number>();

  while (
    mapConversationMessageWindow(window).length === 0 &&
    window.hasMore &&
    window.nextBeforeId !== null &&
    !visitedCursors.has(window.nextBeforeId) &&
    pagesFetched < maxPages
  ) {
    const beforeId = window.nextBeforeId;
    visitedCursors.add(beforeId);
    window = prependOlderConversationMessagePage(
      window,
      await fetchOlder(beforeId),
    );
    pagesFetched += 1;
    onWindowChange?.(window);
  }

  return {
    window,
    pagesFetched,
    reachedLimit:
      pagesFetched >= maxPages &&
      mapConversationMessageWindow(window).length === 0,
  };
}

export function mergeNewestConversationMessagePage(
  current: ConversationMessageWindow,
  page: ConversationMessagePage,
): ConversationMessageWindow {
  // A legacy response is the whole conversation, so it is authoritative for
  // removals as well as additions. This also keeps a rollback to the old API
  // safe while the four-repository rollout is in flight.
  if (page.legacyComplete) {
    return replaceConversationMessageWindow(page);
  }

  if (page.items.length === 0 && !page.hasMore) {
    return replaceConversationMessageWindow(page);
  }

  const newestRetainedId = sortableMessageId(
    current.rawMessages[current.rawMessages.length - 1],
  );
  const oldestPolledId = sortableMessageId(page.items[0]);
  const hasForwardGap =
    current.rawMessages.length > 0 &&
    page.items.length > 0 &&
    !rawMessagesOverlap(current.rawMessages, page.items) &&
    Number.isFinite(newestRetainedId) &&
    Number.isFinite(oldestPolledId) &&
    oldestPolledId > newestRetainedId;

  if (hasForwardGap) {
    const cachedOlderSegments = page.hasMore
      ? [
          ...current.cachedOlderSegments,
          {
            rawMessages: current.rawMessages,
            hasMore: current.hasMore,
            nextBeforeId: current.nextBeforeId,
            startsMidCluster: current.startsMidCluster,
            legacyComplete: current.legacyComplete,
          },
        ]
      : [];
    return {
      ...replaceConversationMessageWindow(page),
      cachedOlderSegments,
    };
  }

  return {
    ...current,
    rawMessages: mergeRawMessages(current.rawMessages, page.items),
    total: page.total,
    // Once a legacy response loaded the complete history, a later opt-in poll
    // cannot make that already-loaded history incomplete.
    legacyComplete: current.legacyComplete,
  };
}

export function prependOlderConversationMessagePage(
  current: ConversationMessageWindow,
  page: ConversationMessagePage,
): ConversationMessageWindow {
  if (page.legacyComplete) {
    return replaceConversationMessageWindow(page);
  }

  let rawMessages = mergeRawMessages(page.items, current.rawMessages);
  const cachedOlderSegments = [...current.cachedOlderSegments];
  let boundary: Omit<ConversationMessageSegment, "rawMessages"> = {
    hasMore: page.hasMore,
    nextBeforeId: page.nextBeforeId,
    startsMidCluster: page.startsMidCluster,
    legacyComplete: page.legacyComplete,
  };

  // Reattach cached contiguous domains from newest to oldest only when an
  // overlapping raw ID proves the hole between them has been closed.
  while (cachedOlderSegments.length > 0) {
    const nearest = cachedOlderSegments[cachedOlderSegments.length - 1];
    if (!rawMessagesOverlap(rawMessages, nearest.rawMessages)) break;
    cachedOlderSegments.pop();
    rawMessages = mergeRawMessages(nearest.rawMessages, rawMessages);
    boundary = {
      hasMore: nearest.hasMore,
      nextBeforeId: nearest.nextBeforeId,
      startsMidCluster: nearest.startsMidCluster,
      legacyComplete: nearest.legacyComplete,
    };
  }

  if (!page.hasMore) {
    // If the authoritative history reaches its beginning without overlapping,
    // any still-disconnected cache contains rows that no longer exist.
    cachedOlderSegments.length = 0;
    boundary = {
      hasMore: false,
      nextBeforeId: null,
      startsMidCluster: page.startsMidCluster,
      legacyComplete: page.legacyComplete,
    };
  }

  return {
    ...current,
    rawMessages,
    cachedOlderSegments,
    total: page.total,
    ...boundary,
  };
}

export function mapConversationMessageWindow(
  window: ConversationMessageWindow,
): IDAChatMessage[] {
  let renderableRawMessages = window.rawMessages;
  const isHiddenFromChat = (message: HubConversationMessage) =>
    "visible_in_chat" in message && message.visible_in_chat === false;
  const firstVisibleIndex = window.rawMessages.findIndex(
    (message) => !isHiddenFromChat(message),
  );
  const firstVisibleMessage = window.rawMessages[firstVisibleIndex];
  const firstVisibleRole =
    firstVisibleMessage && "role" in firstVisibleMessage
      ? firstVisibleMessage.role
      : undefined;
  // The Hub's starts_mid_cluster metadata describes the first raw row. If a
  // page begins with hidden rows, that can be a hidden user context row even
  // though the first visible row continues an assistant/tool cluster from an
  // older page. Keep that cluster deferred while older history remains.
  const startsWithHiddenPrefixBeforeAssistantCluster =
    window.hasMore &&
    firstVisibleIndex > 0 &&
    (firstVisibleRole === "assistant" || firstVisibleRole === "tool");

  if (window.startsMidCluster || startsWithHiddenPrefixBeforeAssistantCluster) {
    const firstCompleteBoundary = window.rawMessages.findIndex(
      (message) =>
        !isHiddenFromChat(message) &&
        message.role !== "assistant" &&
        message.role !== "tool",
    );
    renderableRawMessages =
      firstCompleteBoundary === -1
        ? []
        : window.rawMessages.slice(firstCompleteBoundary);
  }

  return mapConversationMessages(renderableRawMessages);
}

export function removeConversationMessagesFrom(
  window: ConversationMessageWindow,
  hubMessageId: number,
): ConversationMessageWindow {
  if (!isPositiveSafeInteger(hubMessageId)) return window;

  return {
    ...window,
    rawMessages: window.rawMessages.filter((message) => {
      const id = sortableMessageId(message);
      return !Number.isFinite(id) || id < hubMessageId;
    }),
    cachedOlderSegments: window.cachedOlderSegments
      .map((segment) => ({
        ...segment,
        rawMessages: segment.rawMessages.filter((message) => {
          const id = sortableMessageId(message);
          return !Number.isFinite(id) || id < hubMessageId;
        }),
      }))
      .filter((segment) => segment.rawMessages.length > 0),
  };
}
